import type { DiGraph } from "../graph/digraph.js";
import type { EloRating } from "../types.js";
import type { PlayerState } from "../session.js";
import type { MasteryState } from "../mastery/masteryState.js";
import type { EnginePersistence, GameType } from "../persistence/types.js";
import { effectivePlayerForConcept, updateMasteryAfterAttempt } from "../mastery/masteryState.js";
import { newPlayerK, updateAfterAttempt } from "../elo.js";

/**
 * One sub-answer emitted by a mini-game. `weight ∈ [0, 1]` (clamped) lets the game
 * express partial credit; default is `1` (a full attempt).
 *
 * `questionRating` defaults to the player’s **effective rating for the concept**, i.e.
 * a "fair-match" baseline so that emitting equal correct/incorrect events at default
 * difficulty is a no-op on Elo on average.
 */
export type GameEvent = {
  conceptId: string;
  correct: boolean;
  weight?: number;
  gameType: GameType;
  questionRating?: EloRating;
};

export type RecordGameEventArgs = {
  graph: DiGraph;
  player: PlayerState;
  mastery: MasteryState;
  persistence: EnginePersistence;
  event: GameEvent;
  /** Mastery blend used to compute the default `questionRating`. */
  blend?: number;
  /** Optional clock injection (for tests). Defaults to `Date.now`. */
  now?: () => Date;
};

/**
 * Atomically: update player Elo, update per-concept mastery (with weight), and append
 * an `AttemptRecord` to persistence. Returns the new `player` and `mastery`. The
 * caller is responsible for passing the new state back into the next event so that
 * weighted updates compound correctly within a round.
 */
export function recordGameEvent(args: RecordGameEventArgs): {
  player: PlayerState;
  mastery: MasteryState;
} {
  const { graph, player, mastery, persistence, event } = args;
  const w = Math.max(0, Math.min(1, event.weight ?? 1));
  const blend = args.blend ?? 0.45;
  const now = (args.now ?? (() => new Date()))();

  const conceptRating = effectivePlayerForConcept(player.rating, event.conceptId, mastery, blend);
  const qRating = event.questionRating ?? conceptRating;
  const k = newPlayerK(32, player.gamesPlayed) * w;
  const { player: newR } = updateAfterAttempt(player.rating, qRating, event.correct, k);

  const nextPlayer: PlayerState = {
    rating: newR,
    gamesPlayed: player.gamesPlayed + 1,
  };
  const nextMastery = updateMasteryAfterAttempt(graph, mastery, event.conceptId, event.correct, w);

  persistence.savePlayer(nextPlayer);
  persistence.appendAttempt({
    at: now.toISOString(),
    questionId: `${event.gameType}-${event.conceptId}-${now.getTime()}`,
    conceptId: event.conceptId,
    correct: event.correct,
    playerRatingAfter: nextPlayer.rating,
    weight: w,
    gameType: event.gameType,
  });

  return { player: nextPlayer, mastery: nextMastery };
}
